import { Injectable } from '@angular/core';
import { Invoices, InvoicesList, SingleInvoiceResponse, WebserviceResponse} from "../interfaces";
import { HttpClient, HttpErrorResponse, HttpParams } from "@angular/common/http";
import { AlertService, IAlertType } from "./alert.service";
import { Observable, throwError } from "rxjs";
import { catchError, retry } from "rxjs/operators";
import { restBaseUrl } from "../utils/rest-url";

@Injectable({
  providedIn: 'root'
})
export class InvoicesStoreService {
  invoices: Invoices[] = [];

  private readonly endpoint = restBaseUrl('/invoices');

  constructor(
      private http: HttpClient,
      private alertDispatcher: AlertService
  ) { }

  private handleError(error: HttpErrorResponse){
    let errorMessage = 'Unknown error!';
    if (error.error instanceof ErrorEvent) {
      // Client-side errors
      errorMessage = `Client-side error: ${error.error.message}`;
    } else {
      // Server-side errors
      errorMessage = `Server-side error code: ${error.status} - message: ${error.message}`;
    }
    // window.alert(errorMessage);

    this.alertDispatcher.show({
      message: errorMessage,
      clrAlertType: IAlertType.DANGER
    }, 5000);

    // return throwError(errorMessage); deprecated
    return throwError(() => new Error(errorMessage));
  }

  // Main CRUD Services

  public createInvoice(prid: string, jobid: number, delieveryid: number):Observable<WebserviceResponse<any>> {
    return this.http.post<WebserviceResponse<any>>(
        `${this.endpoint}/add_invoices?job_id=${jobid}&delivery_id=${delieveryid}&project_id=${prid}`,
        { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public getInvoices(praefix: number, invoiceID: number, projectnumber: string, jobID: number, deliveryID: number, state: string): Observable<WebserviceResponse<Invoices[]>> {
    const options = new HttpParams({
      fromObject: {
        praefix: praefix,
        number: invoiceID,
        project_id: projectnumber,
        job_id: jobID,
        delivery_id: deliveryID,
        status: state
      }
    });
    return this.http.get<WebserviceResponse<Invoices[]>>(this.endpoint + '/get_invoices',
        { params: options }).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public getSingleInvoice(praefix: number, invoiceid: number): Observable<SingleInvoiceResponse> {
    const options = new HttpParams({
      fromObject: {
        praefix: praefix,
        number: invoiceid
      }
    });
    return this.http.get<SingleInvoiceResponse>(this.endpoint + '/get_single_invoice',
        { params: options }).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public updateDueDate(invoiceID: string, dueDate: object) {
    if (!('newDueDate' in dueDate)) {
      throw new Error('Invalid dueDate object');
    }
    let dateII = dueDate.newDueDate;

    return this.http.put(
        `${this.endpoint}/update_duedate?invoice_id=${invoiceID}&dateII=${dateII}`,
        { responseType: 'json'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public updatePaid(invoiceID: string, paidDate: object) {
    if (!('amountPaid' in paidDate)) {
      throw new Error('Invalid dueDate object');
    }
    let saldo = paidDate.amountPaid;
    if (!('paidDate' in paidDate)) {
      throw new Error('Invalid dueDate object');
    }
    let dateIII = paidDate.paidDate;
    return this.http.put(
        `${this.endpoint}/update_paid?invoice_id=${invoiceID}&paid=${saldo}&dateIII=${dateIII}`,
        { responseType: 'json'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public updateAmounts(invoiceID: string, brutto: number, skonto: number) {
    return this.http.put(
        `${this.endpoint}/update_skonto_brutto?invoice_id=${invoiceID}&brutto=${brutto}&skonto=${skonto}`,
        { responseType: 'json'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public deleteInvoice(praefix: string, invoiceID: string){
    return this.http.delete(
        `${this.endpoint}/delete_invoices?invoice_id=${praefix}.${invoiceID}`,
        { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public cancelInvoice(praefix: string, invoiceID: string): Observable<WebserviceResponse<any>> {
    return this.http.post<WebserviceResponse<any>>(
        `${this.endpoint}/cancel_invoice?invoice_id=${praefix}.${invoiceID}`,
        {}
    ).pipe(
        retry(1),
        catchError(this.handleError.bind(this))
    );
  }

  // Invoice Position Services

  public getInvoicesPositions(invoiceID: string, positionID: number, partID: number): Observable<WebserviceResponse<InvoicesList[]>> {
    const options = new HttpParams({
      fromObject: {
        invoice_number: invoiceID,
        position: positionID,
        part: partID
      }
    });
    return this.http.get<WebserviceResponse<InvoicesList[]>>(this.endpoint + '/get_invoices_position',
        { params: options }).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public updateInvoicesPosition(invoice: InvoicesList) {
    return this.http.put(
        `${this.endpoint}/update_invoices_position`,
        invoice,
        { responseType: 'json'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public deleteInvoicePosition(invoiceID: string, positionID: number, partID: number) {
    return this.http.delete(
        `${this.endpoint}/delete_invoice_position?invoice_number=${invoiceID}&position=${positionID}&part=${partID}`,
        { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  // Invoice Status

  public updateInvoiceStatus(invoiceID: string, status: string){
    return this.http.put(
        `${this.endpoint}/update_status?invoice_id=${invoiceID}&status=${status}`,
        { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }
}
